import { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
  FileText,
  Download,
  Printer,
  RefreshCw,
  AlertCircle,
  Calendar,
  ChevronDown,
  FileJson,
  FileSpreadsheet,
} from 'lucide-react';
import {
  useReport,
  getDateRangeFromPreset,
  getDefaultDateRange,
  downloadReportAsJSON,
  downloadReportAsCSV,
  REPORT_TYPE_LABELS,
  DATE_PRESET_LABELS,
  type ReportType,
  type DateRangePreset,
  type ReportData,
  type ProjectStatusReport,
  type CapacityReport,
  type TimeTrackingReport,
} from '../hooks/useReports';

/**
 * Reports page - allows users to generate and export various reports.
 */
export function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>('project-status');
  const [datePreset, setDatePreset] = useState<DateRangePreset>('this-week');
  const [customDateRange, setCustomDateRange] = useState(() => getDefaultDateRange());
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  // Calculate actual date range based on preset or custom selection
  const dateRange = useMemo(() => {
    if (datePreset === 'custom') {
      return customDateRange;
    }
    return getDateRangeFromPreset(datePreset);
  }, [datePreset, customDateRange]);

  // Fetch report data
  const { data, isLoading, isError, error, refetch, isFetching } = useReport({
    reportType,
    startDate: dateRange.start,
    endDate: dateRange.end,
    enabled: true,
  });

  const handleExportJSON = () => {
    if (data) {
      downloadReportAsJSON(data, reportType);
    }
  };

  const handleExportCSV = () => {
    if (data) {
      downloadReportAsCSV(data, reportType);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h2 className="text-2xl font-semibold">Reports</h2>
          <p className="text-sm text-foreground-muted mt-1">
            Generate and export reports for project status, capacity, and time tracking.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4 print:hidden">
        {/* Report Type Selector */}
        <div className="relative">
          <label className="block text-sm font-medium text-foreground-muted mb-1.5">
            Report Type
          </label>
          <button
            onClick={() => {
              setIsTypeDropdownOpen(!isTypeDropdownOpen);
              setIsDateDropdownOpen(false);
            }}
            className="flex items-center justify-between gap-2 w-56 px-3 py-2 text-sm border border-border rounded-md bg-background hover:bg-background-subtle transition-colors"
          >
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-foreground-muted" />
              {REPORT_TYPE_LABELS[reportType]}
            </span>
            <ChevronDown className="h-4 w-4 text-foreground-muted" />
          </button>
          {isTypeDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsTypeDropdownOpen(false)}
              />
              <div className="absolute z-20 mt-1 w-56 bg-background border border-border rounded-md shadow-lg">
                {(Object.keys(REPORT_TYPE_LABELS) as ReportType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setReportType(type);
                      setIsTypeDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-background-subtle transition-colors first:rounded-t-md last:rounded-b-md ${
                      reportType === type ? 'bg-primary/10 text-primary' : ''
                    }`}
                  >
                    {REPORT_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Date Range Selector */}
        <div className="relative">
          <label className="block text-sm font-medium text-foreground-muted mb-1.5">
            Date Range
          </label>
          <button
            onClick={() => {
              setIsDateDropdownOpen(!isDateDropdownOpen);
              setIsTypeDropdownOpen(false);
            }}
            className="flex items-center justify-between gap-2 w-48 px-3 py-2 text-sm border border-border rounded-md bg-background hover:bg-background-subtle transition-colors"
          >
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-foreground-muted" />
              {DATE_PRESET_LABELS[datePreset]}
            </span>
            <ChevronDown className="h-4 w-4 text-foreground-muted" />
          </button>
          {isDateDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsDateDropdownOpen(false)}
              />
              <div className="absolute z-20 mt-1 w-48 bg-background border border-border rounded-md shadow-lg">
                {(Object.keys(DATE_PRESET_LABELS) as DateRangePreset[]).map((preset) => (
                  <button
                    key={preset}
                    onClick={() => {
                      setDatePreset(preset);
                      setIsDateDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-background-subtle transition-colors first:rounded-t-md last:rounded-b-md ${
                      datePreset === preset ? 'bg-primary/10 text-primary' : ''
                    }`}
                  >
                    {DATE_PRESET_LABELS[preset]}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Custom Date Inputs (shown when custom is selected) */}
        {datePreset === 'custom' && (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground-muted mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({ ...prev, start: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-muted mb-1.5">
                End Date
              </label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({ ...prev, end: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </>
        )}

        {/* Refresh Button */}
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-background-subtle transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Export Actions */}
        {data && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportJSON}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-background-subtle transition-colors"
              title="Export as JSON"
            >
              <FileJson className="h-4 w-4" />
              JSON
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-background-subtle transition-colors"
              title="Export as CSV"
            >
              <FileSpreadsheet className="h-4 w-4" />
              CSV
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary-hover transition-colors"
              title="Print Report"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
          </div>
        )}
      </div>

      {/* Date Range Display */}
      <div className="text-sm text-foreground-muted print:text-foreground print:font-medium">
        <span className="hidden print:inline">{REPORT_TYPE_LABELS[reportType]} - </span>
        Report period: {formatDisplayDate(dateRange.start)} to {formatDisplayDate(dateRange.end)}
      </div>

      {/* Content */}
      {isLoading ? (
        <ReportLoading />
      ) : isError ? (
        <ReportError message={error.message} onRetry={() => refetch()} />
      ) : data ? (
        <ReportContent reportType={reportType} data={data} />
      ) : (
        <EmptyState />
      )}

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-border text-xs text-foreground-muted">
        <p>Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
        <p>Production Scheduler Dashboard</p>
      </div>
    </div>
  );
}

function ReportContent({ reportType, data }: { reportType: ReportType; data: ReportData }) {
  switch (reportType) {
    case 'project-status':
      return <ProjectStatusReportTable data={data as ProjectStatusReport} />;
    case 'capacity':
      return <CapacityReportTable data={data as CapacityReport} />;
    case 'time-tracking':
      return <TimeTrackingReportTable data={data as TimeTrackingReport} />;
    default:
      return null;
  }
}

function ProjectStatusReportTable({ data }: { data: ProjectStatusReport }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
        <SummaryCard label="Total Orders" value={data.summary.totalOrders} />
        <SummaryCard
          label="On Track"
          value={data.summary.onTrack}
          variant="success"
        />
        <SummaryCard
          label="At Risk"
          value={data.summary.atRisk}
          variant={data.summary.atRisk > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Orders Table */}
      <div className="border border-border rounded-lg overflow-hidden print:border-gray-300">
        <table className="w-full">
          <thead>
            <tr className="bg-background-subtle border-b border-border print:bg-gray-100">
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Order #
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Promised
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Expected
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Status
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground-muted print:text-gray-700">
                Progress
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border print:divide-gray-300">
            {data.orders.map((order) => {
              const progress =
                order.totalTasks > 0
                  ? Math.round((order.completedTasks / order.totalTasks) * 100)
                  : 0;
              const isAtRisk =
                order.expectedShipDate &&
                order.promisedShipDate &&
                order.expectedShipDate > order.promisedShipDate;

              return (
                <tr
                  key={order.gpSalesOrderNumber}
                  className="hover:bg-background-subtle transition-colors print:hover:bg-transparent"
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm">{order.gpSalesOrderNumber}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">{order.customerName}</td>
                  <td className="px-4 py-3 text-sm">
                    {order.promisedShipDate ? formatDisplayDate(order.promisedShipDate) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {order.expectedShipDate ? formatDisplayDate(order.expectedShipDate) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        isAtRisk
                          ? 'bg-danger-subtle text-danger-foreground'
                          : 'bg-success-subtle text-success-foreground'
                      }`}
                    >
                      {isAtRisk ? 'At Risk' : 'On Track'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm">
                      {order.completedTasks}/{order.totalTasks} ({progress}%)
                    </span>
                  </td>
                </tr>
              );
            })}
            {data.orders.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-foreground-muted"
                >
                  No orders found for the selected period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CapacityReportTable({ data }: { data: CapacityReport }) {
  const totalMinutes = data.utilization.reduce((sum, u) => sum + (u.totalMinutes || 0), 0);
  const totalTasks = data.utilization.reduce((sum, u) => sum + (u.tasksWorked || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
        <SummaryCard label="Total People" value={data.utilization.length} />
        <SummaryCard
          label="Total Hours"
          value={formatHours(totalMinutes)}
          suffix="hrs"
        />
        <SummaryCard label="Tasks Completed" value={totalTasks} />
      </div>

      {/* Utilization Table */}
      <div className="border border-border rounded-lg overflow-hidden print:border-gray-300">
        <table className="w-full">
          <thead>
            <tr className="bg-background-subtle border-b border-border print:bg-gray-100">
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Role
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground-muted print:text-gray-700">
                Hours Worked
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground-muted print:text-gray-700">
                Tasks Completed
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border print:divide-gray-300">
            {data.utilization.map((person, idx) => (
              <tr
                key={`${person.name}-${idx}`}
                className="hover:bg-background-subtle transition-colors print:hover:bg-transparent"
              >
                <td className="px-4 py-3 text-sm font-medium">{person.name}</td>
                <td className="px-4 py-3 text-sm text-foreground-muted capitalize">
                  {person.role?.replace(/_/g, ' ') || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  {formatHours(person.totalMinutes || 0)}
                </td>
                <td className="px-4 py-3 text-sm text-right">{person.tasksWorked || 0}</td>
              </tr>
            ))}
            {data.utilization.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-foreground-muted"
                >
                  No capacity data found for the selected period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TimeTrackingReportTable({ data }: { data: TimeTrackingReport }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2">
        <SummaryCard label="Total Entries" value={data.summary.totalEntries} />
        <SummaryCard
          label="Total Hours"
          value={data.summary.totalHours}
          suffix="hrs"
        />
      </div>

      {/* Time Entries Table */}
      <div className="border border-border rounded-lg overflow-hidden print:border-gray-300">
        <table className="w-full">
          <thead>
            <tr className="bg-background-subtle border-b border-border print:bg-gray-100">
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Person
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Order #
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Part Number
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Labor Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground-muted print:text-gray-700">
                Start Time
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground-muted print:text-gray-700">
                Duration
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border print:divide-gray-300">
            {data.entries.map((entry) => (
              <tr
                key={entry.id}
                className="hover:bg-background-subtle transition-colors print:hover:bg-transparent"
              >
                <td className="px-4 py-3 text-sm font-medium">{entry.personName}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-sm">{entry.gpSalesOrderNumber}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-sm">{entry.partNumber}</span>
                </td>
                <td className="px-4 py-3 text-sm text-foreground-muted capitalize">
                  {entry.laborBucket?.replace(/_/g, ' ') || '-'}
                </td>
                <td className="px-4 py-3 text-sm">{formatDateTime(entry.startTime)}</td>
                <td className="px-4 py-3 text-sm text-right">
                  {entry.durationMinutes ? formatDuration(entry.durationMinutes) : 'In Progress'}
                </td>
              </tr>
            ))}
            {data.entries.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-foreground-muted"
                >
                  No time entries found for the selected period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  suffix,
  variant = 'default',
}: {
  label: string;
  value: string | number;
  suffix?: string;
  variant?: 'default' | 'success' | 'danger';
}) {
  const variantClasses = {
    default: 'bg-background-subtle',
    success: 'bg-success-subtle',
    danger: 'bg-danger-subtle',
  };

  return (
    <div
      className={`rounded-lg p-4 border border-border print:border-gray-300 ${variantClasses[variant]}`}
    >
      <p className="text-sm text-foreground-muted print:text-gray-600">{label}</p>
      <p className="text-2xl font-semibold mt-1">
        {value}
        {suffix && <span className="text-sm text-foreground-muted ml-1">{suffix}</span>}
      </p>
    </div>
  );
}

function ReportLoading() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-lg p-4 border border-border">
            <div className="h-4 w-24 bg-background-muted rounded animate-pulse" />
            <div className="h-8 w-16 bg-background-muted rounded animate-pulse mt-2" />
          </div>
        ))}
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-background-subtle border-b border-border px-4 py-3">
          <div className="h-4 w-full bg-background-muted rounded animate-pulse" />
        </div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border last:border-0">
            <div className="h-4 w-full bg-background-muted rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-danger mb-4" />
      <h3 className="text-lg font-medium">Failed to load report</h3>
      <p className="text-sm text-foreground-muted mt-1 max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover transition-colors text-sm font-medium"
      >
        Try Again
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center border border-border rounded-lg">
      <FileText className="h-12 w-12 text-foreground-muted mb-4" />
      <h3 className="text-lg font-medium">No report data</h3>
      <p className="text-sm text-foreground-muted mt-1">
        Select a report type and date range to generate a report.
      </p>
    </div>
  );
}

// Utility functions
function formatDisplayDate(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

function formatDateTime(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM d, h:mm a');
  } catch {
    return dateString;
  }
}

function formatHours(minutes: number): string {
  const hours = minutes / 60;
  return hours.toFixed(1);
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) {
    return `${mins}m`;
  }
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
